import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Component, forwardRef, inject, input, signal, WritableSignal } from '@angular/core';

import { MatFormField, MatFormFieldAppearance, MatLabel } from '@angular/material/form-field';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { IsoLanguage, isoLanguages } from '@ird/shared-utils';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInput, MatChipOption, MatChipRemove } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'shared-language-multiselect',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatOption,
    TranslatePipe,
    FlexLayoutModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatChipGrid,
    MatIcon,
    MatChipInput,
    MatChipOption,
    MatChipRemove,
  ],
  templateUrl: './language-multiselect.component.html',
  styleUrl: './language-multiselect.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LanguageMultiselectComponent),
      multi: true,
    },
  ],
})
export class LanguageMultiselectComponent implements ControlValueAccessor {
  public readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  public matAppearance = input<MatFormFieldAppearance>('fill');
  public hint = input<string>(null);
  public returnObject = input<'full' | 'codes'>('full');
  public languages: WritableSignal<IsoLanguage[]> = signal([]);
  public filteredLanguages: WritableSignal<IsoLanguage[]> = signal(isoLanguages);
  public lang = new FormControl<string>({ value: '', disabled: false });
  private readonly announcer = inject(LiveAnnouncer);
  private onChange: (value: IsoLanguage[] | string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private isDisabled = false;

  constructor() {
    this.lang.valueChanges.pipe(takeUntilDestroyed()).subscribe((searchText) => {
      if (typeof searchText !== 'string') {
        return;
      }

      if (searchText) {
        const languages = isoLanguages.filter((l) => l.name.toLowerCase().includes(searchText.toLowerCase()));
        this.filteredLanguages.set(languages);
      } else {
        this.filteredLanguages.set(isoLanguages);
      }
    });
  }

  writeValue(obj: string[] | IsoLanguage[] | null): void {
    if (!obj || obj.length === 0) {
      this.languages.set([]);
      return;
    }
    const firstLanguage: string | IsoLanguage = obj[0];

    if (typeof firstLanguage === 'string') {
      const languageObjects = isoLanguages.filter((l) => (obj as string[]).includes(l.code));
      this.languages.set(languageObjects);
    } else if (typeof firstLanguage === 'object' && firstLanguage !== null && 'code' in firstLanguage) {
      this.languages.set(obj as IsoLanguage[]);
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.lang.disable();
    } else {
      this.lang.enable();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  remove(language: IsoLanguage): void {
    if (this.isDisabled) {
      return;
    }

    this.languages.update((languages) => {
      const index = languages.indexOf(language);
      if (index < 0) {
        return languages;
      }

      const newLanguages = [...languages];
      newLanguages.splice(index, 1);
      this.announcer.announce(`Removed ${language.name}`);

      this.emitChange(newLanguages);
      this.onTouched();

      return newLanguages;
    });
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (this.isDisabled) {
      return;
    }

    const language = event.option.value as IsoLanguage;

    if (this.languages().some((l) => l.code === language.code)) {
      this.lang.setValue('');
      event.option.deselect();
      return;
    }

    this.languages.update((value) => {
      const newLanguages = [...value, language];

      this.emitChange(newLanguages);
      this.onTouched();

      return newLanguages;
    });

    this.lang.setValue('');
    event.option.deselect();
  }

  private emitChange(languages: IsoLanguage[]): void {
    if (languages.length === 0) {
      this.onChange([]);
    } else if (this.returnObject() === 'codes') {
      const languagesCodes = languages.map((l) => l.code);
      this.onChange(languagesCodes);
    } else {
      this.onChange(languages);
    }
  }
}
